import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Typography from '~components/typography';
import {
  AmendmentDetailOption,
  BookingStatusOption,
  PaymentItemStatusOptions,
} from '~graphql/__generated__/globalTypes';
import { SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings } from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { Trans, useTranslation } from '~i18n';
import { Time } from '~svg-components';
import { amendmentCountDown } from '~utils/count-down-time';

interface IProps {
  booking: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings;
}

const latestAmendmentDetailTypes = [
  AmendmentDetailOption.TENANCY_SHORTEN_EARLY_CHECK_OUT,
  AmendmentDetailOption.INSTALLMENT,
];

const BookingCardAmendment: React.FC<IProps> = ({ booking }) => {
  const { t } = useTranslation(['student_portal']);

  const { id, targetBookings, status, countDown, heldToTime, latestAmendmentDetail } = booking;

  const [startTime, setStartTime] = useState<any>(moment());
  const [countDownResult, setCountDownResult] = useState('00:00');

  const targetBooking = useMemo(() => {
    return targetBookings?.find(item => {
      return (
        item?.status === BookingStatusOption.SELECTION_COMPLETED ||
        item?.status === BookingStatusOption.DETAILS_COMPLETED ||
        item?.status === BookingStatusOption.TERMS_ACCEPTED
      );
    });
  }, [targetBookings]);

  const payDueConfirmPaymentItemIsAwaiting = useMemo(() => {
    const targeBooking = targetBookings?.find(item => {
      return item?.status === BookingStatusOption.TERMS_ACCEPTED;
    });

    return !!targeBooking?.paymentItems?.find(
      item => item?.status === PaymentItemStatusOptions.AWAITING
    );
  }, [targetBookings]);

  const getTargetCountDown = useMemo(() => {
    if (
      (status === BookingStatusOption.RESIDENT &&
        latestAmendmentDetail?.type &&
        latestAmendmentDetailTypes.includes(latestAmendmentDetail?.type)) ||
      latestAmendmentDetail?.targetBookingId === id
    ) {
      return countDown;
    }

    return targetBooking?.countDown;
  }, [countDown, id, latestAmendmentDetail, status, targetBooking]);

  const getTargetHeldTime = useMemo(() => {
    if (
      status === BookingStatusOption.RESIDENT &&
      latestAmendmentDetail?.type &&
      latestAmendmentDetailTypes.includes(latestAmendmentDetail?.type)
    ) {
      return heldToTime;
    }
    return targetBooking?.heldToTime;
  }, [heldToTime, latestAmendmentDetail, status, targetBooking]);

  useEffect(() => {
    if (!getTargetHeldTime && getTargetCountDown) {
      const leftTime = amendmentCountDown(startTime, moment(getTargetCountDown));
      const timer = setInterval(() => {
        setStartTime(moment());
      }, 30000);
      if (leftTime === 0) {
        clearInterval(timer);
        setCountDownResult('00:00');
        return;
      }
      setCountDownResult(leftTime);
      return () => {
        clearInterval(timer);
      };
    }
  }, [getTargetCountDown, getTargetHeldTime, startTime]);

  const bankTransferCountDwon = useMemo(() => {
    return (
      <div className="bank-transfer-countdown">
        <Time className="bank-transfer-countdown__icon" />
        <Trans ns="student_portal" i18nKey="amendment.bank_transfer_countdown">
          Please make your bank transfer payment within
          <strong style={{ color: 'var(--color-red_6)' }}>{{ time: countDownResult }}</strong>
          to confirm your amendment. If you have already paid, please wait patiently for the payment
          result.
        </Trans>
        <style jsx>{`
          .bank-transfer-countdown {
            padding-left: 28px;
            position: relative;
            :global(&__icon) {
              width: 20px;
              height: 19px;
              fill: var(--color-red_6);
              position: absolute;
              left: 0;
              top: 5px;
            }
          }
        `}</style>
      </div>
    );
  }, [countDownResult]);

  const amednmentHeldTime = useMemo(() => {
    return (
      <Trans ns="student_portal" i18nKey="amendment.held_time">
        You have till
        <strong style={{ color: 'var(--color-red_6)' }}>
          {{ time: moment(getTargetHeldTime).format('lll') }}
        </strong>
        left to respond to the amendment. Otherwise, the amendment will not take effect.
      </Trans>
    );
  }, [getTargetHeldTime]);

  const amendmentCountDownNode = useMemo(() => {
    return (
      <Trans ns="student_portal" i18nKey="amendment.count_down">
        You have
        <strong style={{ color: 'var(--color-red_6)' }}>{{ time: countDownResult }}</strong>
        left to respond to the amendment. Otherwise, the amendment will not take effect."
      </Trans>
    );
  }, [countDownResult]);

  return (
    <div className="booking-card-amendment">
      {!payDueConfirmPaymentItemIsAwaiting && (
        <Typography variant="h9" component="p" style={{ marginBottom: 4 }}>
          {latestAmendmentDetail?.type === AmendmentDetailOption.GUARANTOR
            ? t('amendment.change_guarantor.title')
            : t('amendment.title')}
        </Typography>
      )}

      {getTargetHeldTime && (
        <Typography component="p" variant="body3">
          {payDueConfirmPaymentItemIsAwaiting ? bankTransferCountDwon : amednmentHeldTime}
        </Typography>
      )}
      {!getTargetHeldTime && getTargetCountDown && (
        <Typography component="p" variant="body3">
          {payDueConfirmPaymentItemIsAwaiting ? bankTransferCountDwon : amendmentCountDownNode}
        </Typography>
      )}

      <style jsx>{`
        .booking-card-amendment {
          width: 480px;
          margin-top: 20px;
          border-radius: 14px;
          padding: 12px;
          background-color: ${payDueConfirmPaymentItemIsAwaiting
            ? 'var(--color-pink_1)'
            : 'var(--color-purple_1)'};
        }
        @media screen and (max-width: 768px) {
          .booking-card-amendment {
            width: 100%;
            margin-top: 16px;
            padding: 8px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default BookingCardAmendment;
